import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import {
    Fab,
    Drawer,
    Box,
    Typography,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Tooltip,
    Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChecklistIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import * as MuiIcons from '@mui/icons-material';

import {
    fetchOnboardingProgress,
    openChecklist,
    closeChecklist,
    startTour,
} from '../../../redux/actions/onboarding';
import {
    filterStepsByAudience,
    detectAudience,
    isUserOnTrial,
    AUDIENCES,
    STEP_IDS,
} from '../configs/onboarding.config';

class ChecklistFab extends Component {
    componentDidMount() {
        const { onboarding, userSession } = this.props;
        // Gate trial: nao consultar API pra hirer pago (evita 1 GET inutil por login)
        if (
            userSession?.user &&
            isUserOnTrial(userSession.user) &&
            !onboarding?.loaded &&
            !onboarding?.loading
        ) {
            this.props.fetchOnboardingProgress();
        }
    }

    componentDidUpdate(prevProps) {
        const prevUser = prevProps.userSession?.user;
        const currUser = this.props.userSession?.user;
        // refetch quando usuario logar (so se trial)
        if (!prevUser && currUser && isUserOnTrial(currUser) && !this.props.onboarding?.loaded) {
            this.props.fetchOnboardingProgress();
        }
    }

    handleStepClick = (step) => {
        const { history, onboarding } = this.props;
        const isCompleted = onboarding?.steps?.find((s) => s.stepId === step.id)?.completed;

        if (isCompleted) return;

        if (step.tourId) {
            // Step com mini-tour: dispara tour Joyride
            this.props.startTour(step.tourId);
        }

        if (step.navigateTo) {
            history.push(step.navigateTo);
        }
        this.props.closeChecklist();
    };

    renderIcon = (iconName) => {
        const Icon = MuiIcons[iconName];
        return Icon ? <Icon /> : <RadioButtonUncheckedIcon />;
    };

    isStepCompleted = (stepId) => {
        const step = this.props.onboarding?.steps?.find((s) => s.stepId === stepId);
        return !!step?.completed;
    };

    render() {
        const { onboarding, userSession, intl } = this.props;
        if (!userSession?.user) return null;

        // Gate principal: SO trials veem o checklist
        if (!isUserOnTrial(userSession.user)) return null;

        // não exibir se já completou tudo OU se o user é só cliente (role 4) — onboarding não se aplica
        const audience = detectAudience(userSession.user);
        if (audience !== AUDIENCES.ADMIN && audience !== AUDIENCES.AGENT) return null;
        if (onboarding?.allCompleted) return null;

        const steps = filterStepsByAudience(audience).sort((a, b) => a.order - b.order);
        const completedCount = steps.filter((s) => this.isStepCompleted(s.id)).length;
        const totalCount = steps.length;
        const progressPercent =
            totalCount === 0 ? 0 : Math.round((completedCount * 100) / totalCount);

        const isOpen = !!onboarding?.isChecklistOpen;

        return (
            <>
                <Tooltip title={intl.formatMessage({ id: 'onboarding.checklist.tooltip' })} placement="left">
                    <Fab
                        onClick={() => this.props.openChecklist()}
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            backgroundColor: '#8BC34A',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#7cb342' },
                            zIndex: 1200,
                        }}
                    >
                        <Badge
                            badgeContent={totalCount - completedCount}
                            color="error"
                            invisible={totalCount === completedCount}
                        >
                            <ChecklistIcon />
                        </Badge>
                    </Fab>
                </Tooltip>

                <Drawer
                    anchor="right"
                    open={isOpen}
                    onClose={() => this.props.closeChecklist()}
                    PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
                >
                    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {intl.formatMessage({ id: 'onboarding.checklist.title' })}
                            </Typography>
                            <IconButton onClick={() => this.props.closeChecklist()}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Typography variant="body2" sx={{ color: '#777', mb: 2 }}>
                            {intl.formatMessage({ id: 'onboarding.checklist.subtitle' })}
                        </Typography>

                        {/* Progress bar */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ color: '#555', fontWeight: 600 }}>
                                    {intl.formatMessage(
                                        { id: 'onboarding.checklist.progress' },
                                        { completed: completedCount, total: totalCount }
                                    )}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#8BC34A', fontWeight: 700 }}>
                                    {progressPercent}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercent}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': { backgroundColor: '#8BC34A' },
                                }}
                            />
                        </Box>

                        {/* Steps list */}
                        <List sx={{ flex: 1, overflowY: 'auto' }}>
                            {steps.map((step) => {
                                const completed = this.isStepCompleted(step.id);
                                return (
                                    <ListItem key={step.id} disablePadding sx={{ mb: 1 }}>
                                        <ListItemButton
                                            onClick={() => this.handleStepClick(step)}
                                            disabled={completed}
                                            sx={{
                                                borderRadius: '12px',
                                                border: '1px solid #e0e0e0',
                                                borderLeft: completed
                                                    ? '4px solid #8BC34A'
                                                    : '4px solid transparent',
                                                backgroundColor: completed ? '#f1f8e9' : 'transparent',
                                                '&:hover': { backgroundColor: completed ? '#f1f8e9' : '#fafafa' },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {completed ? (
                                                    <CheckCircleIcon sx={{ color: '#8BC34A' }} />
                                                ) : (
                                                    <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: completed ? '#558b2f' : '#212121',
                                                            textDecoration: completed ? 'line-through' : 'none',
                                                        }}
                                                    >
                                                        {intl.formatMessage({ id: step.labelKey })}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: '#888', display: 'block', mt: 0.5 }}
                                                    >
                                                        {intl.formatMessage({ id: step.descriptionKey })}
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                </Drawer>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    userSession: state.userSession,
    onboarding: state.onboarding,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        { fetchOnboardingProgress, openChecklist, closeChecklist, startTour },
        dispatch
    );

export default withRouter(injectIntl(connect(mapStateToProps, mapDispatchToProps)(ChecklistFab)));
